import { Component, DestroyRef, Injector, OnDestroy, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { EMPTY, Subscription, TimeoutError, firstValueFrom } from "rxjs";
import { catchError, take, timeout } from "rxjs/operators";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { SafeLocalStorageService } from "src/app/services/safe-local-storage.service";
import { AutoClickButtonComponent } from "../../../components/auto-click-button/auto-click-button.component";

/** Bound wait for continue-step lookups so a hung request cannot leave the CTA disabled forever. */
const CONTINUE_LOOKUP_TIMEOUT_MS = 15000;

@Component({
    selector: "app-account-created",
    templateUrl: "./account-created.component.html",
    styleUrls: ["./account-created.component.scss", "../onesignal.scss"],
    standalone: true,
    imports: [
        IonicModule,
        AutoClickButtonComponent,
        TranslatePipe,
    ],
})
export class AccountCreatedComponent implements OnInit, OnDestroy {
  payment = true;
  routerData: any;
  isRedirected: boolean = false;
  /** Bumped to re-enable the auto-click CTA after a failed continue. */
  ctaResetKey = 0;
  continueError = false;
  showExtraContent = false;
  getPaymentPageData: Data;
  gc: string;
  public TP1: string;
  public TP2: string;
  public TP3: string;
  public T_ID: string;
  vc: any;
  private safeLocalStorage = inject(SafeLocalStorageService);
  private readonly destroyRef = inject(DestroyRef);
  private continueProsubSub: Subscription | undefined;
  private continuePartialRegSub: Subscription | undefined;
  /** Prefetch next-step on page load so a manual CTA click can navigate immediately. */
  private prefetchPromise: Promise<void> | null = null;
  private prefetchedNextStep: string | null = null;
  private prefetchedPartialRegState: unknown;
  private hasPrefetchedPartialReg = false;

  constructor(
    private authService: AuthService,
    public translate: TranslateService,
    private GTMService: GTMSegmentService,
    public angularFireFunctions: AngularFireFunctions,
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector,
    private paymentDataService: PaymentDataService,
  ) {
    // Safely initialize tracking parameters
    this.TP1 = this.safeLocalStorage.getItem("TP1");
    this.TP2 = this.safeLocalStorage.getItem("TP2");
    this.TP3 = this.safeLocalStorage.getItem("TP3");
    this.T_ID = this.safeLocalStorage.getItem("T_ID");
    this.authService.selectedlanguage$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (l) => {
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );
    this.authService?.callSigUpForSalesResponse$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((d) => {
      if (d && location.href.search("account-created") > -1) {
        if (d && d?.response?.status === "FAIL") {
          const proSub = this.safeLocalStorage.getItem("prosub_id");
          this.authService.logout().then();
          this.authService?.callSigUpForSalesResponse$?.next(null);
          if (proSub === null) return;
          this.router.navigateByUrl(`partial-registration?prosub_id=${proSub}`);
          if (d?.response?.message === "MSN_INVALID") {
            const messages = {
              en: "Registration failed, please enter a correct phone number.",
              de: "Die Registrierung ist fehlgeschlagen, bitte geben Sie eine korrekte Telefonnummer ein.",
              fr: "L'inscription a échoué, veuillez saisir un numéro de téléphone correct.",
              it: "Registrazione fallita, si prega di inserire un numero di telefono corretto.",
              es: "La inscripción falló, por favor ingrese un número de teléfono correcto.",
            };
            this.authService.showAnonymousUserMessage(messages[this.authService.selectedlanguage$?.value]);
            return;
          }
          this.authService.showAnonymousUserMessage();
        }
      }
    });
  }

  ngOnInit() {
    const prosubID = this.route?.snapshot?.queryParams?.["prosub_id"];
    this.gc = this.route?.snapshot?.queryParams?.["gc"];
    if (prosubID) {
      this.getPaymentPageData = this.paymentDataService
        ?.getPaymentPageData(prosubID)
        ?.subscribe(
          (res) => {
            if (
              res &&
              res?.optional_page &&
              res?.optional_page == "free-reg-step4h"
            ) {
              this.showExtraContent = true;
            } else {
              this.showExtraContent = false;
            }
          },
          (error) => {
            this.showExtraContent = false;
            return error;
          },
        );
    }
    this.startContinuePrefetch();
  }

  ngOnDestroy(): void {
    this.getPaymentPageData?.unsubscribe();
    this.continueProsubSub?.unsubscribe();
    this.continuePartialRegSub?.unsubscribe();
  }

  async callInt1() {
    if (this.isRedirected) {
      return;
    }
    this.isRedirected = true;
    this.continueError = false;

    const data: any = this.route.snapshot?.queryParams ?? {};
    const tracking = this.buildTrackingQueryParams(data);
    const prosub_id = tracking.prosub_id;

    if (!prosub_id) {
      this.recoverFromContinueFailure("missing prosub_id", { prosub_id: null, next_step: null });
      return;
    }

    try {
      // Prefer already-cached GTM prosub route for an instant navigate.
      const cachedOptionalPage = this.GTMService.prosubDetails?.optional_page;
      if (cachedOptionalPage && !this.prefetchedNextStep) {
        this.prefetchedNextStep = cachedOptionalPage;
      }

      if (this.prefetchPromise) {
        await this.prefetchPromise;
      }

      if (this.prefetchedNextStep) {
        await this.navigateFromPrefetch(tracking);
        return;
      }

      // Prefetch missed — resolve live, but navigate as soon as the next route is known.
      await this.resolveAndNavigateLive(tracking);
    } catch (err) {
      this.recoverFromContinueFailure(err, { prosub_id, next_step: this.prefetchedNextStep });
    }
  }

  private startContinuePrefetch(): void {
    const prosub_id =
      this.route?.snapshot?.queryParams?.["prosub_id"] ??
      this.safeLocalStorage.getItem("prosub_id");
    if (!prosub_id) {
      return;
    }

    this.prefetchPromise = this.runContinuePrefetch(String(prosub_id)).catch((err) => {
      console.error("[account-created] continue prefetch failed", {
        prosub_id,
        error_code: this.getErrorCode(err),
        error: err,
      });
    });
  }

  private async runContinuePrefetch(prosub_id: string): Promise<void> {
    const existingOptional = this.GTMService.prosubDetails?.optional_page;
    if (existingOptional) {
      this.prefetchedNextStep = existingOptional;
      // Product warm is never required to leave Account Created; keep it off the critical path.
      void this.warmProsubDetails(prosub_id);
      if (existingOptional === "free-reg-step4c") {
        await this.prefetchPartialReg(prosub_id);
      }
      return;
    }

    const resInt = await this.withTimeout(
      this.GTMService.callInt1(prosub_id),
      CONTINUE_LOOKUP_TIMEOUT_MS,
      "callInt1"
    );
    if (!resInt) {
      throw Object.assign(new Error("missing next-step route from callInt1"), { code: "MISSING_NEXT_STEP" });
    }
    this.prefetchedNextStep = resInt;

    // Product warm is background-only for every next step (including 4c).
    void this.warmProsubDetails(prosub_id);
    if (resInt === "free-reg-step4c") {
      await this.prefetchPartialReg(prosub_id);
    }
  }

  private async navigateFromPrefetch(tracking: {
    prosub_id: string;
    vc: unknown;
    TP1: string;
    TP2: string;
    TP3: string;
    T_ID: string;
    Other: string;
    gc: unknown;
  }): Promise<void> {
    const nextStep = this.prefetchedNextStep;
    if (!nextStep) {
      throw Object.assign(new Error("missing prefetched next-step"), { code: "MISSING_NEXT_STEP" });
    }

    const queryParams = {
      prosub_id: tracking.prosub_id,
      vc: tracking.vc,
      TP1: tracking.TP1,
      TP2: tracking.TP2,
      TP3: tracking.TP3,
      T_ID: tracking.T_ID,
      Other: tracking.Other,
      gc: tracking.gc,
    };

    if (nextStep === "free-reg-step4c") {
      if (!this.hasPrefetchedPartialReg) {
        await this.prefetchPartialReg(tracking.prosub_id);
      }
      this.navigateToNextStep(nextStep, queryParams, this.prefetchedPartialRegState);
      return;
    }

    this.navigateToNextStep(nextStep, queryParams);
  }

  private async resolveAndNavigateLive(tracking: {
    prosub_id: string;
    vc: unknown;
    TP1: string;
    TP2: string;
    TP3: string;
    T_ID: string;
    Other: string;
    gc: unknown;
  }): Promise<void> {
    const prosub = this.GTMService.prosubDetails;
    const queryParams = {
      prosub_id: tracking.prosub_id,
      vc: tracking.vc,
      TP1: tracking.TP1,
      TP2: tracking.TP2,
      TP3: tracking.TP3,
      T_ID: tracking.T_ID,
      Other: tracking.Other,
      gc: tracking.gc,
    };

    if (prosub?.optional_page) {
      if (prosub.optional_page === "free-reg-step4c") {
        this.getProsubDetails(
          tracking.prosub_id,
          prosub.optional_page,
          tracking.TP1,
          tracking.TP2,
          tracking.TP3,
          tracking.T_ID,
          { vc: tracking.vc, Other: tracking.Other, other: tracking.Other }
        );
        return;
      }
      void this.warmProsubDetails(tracking.prosub_id);
      this.navigateToNextStep(prosub.optional_page, queryParams);
      return;
    }

    const resInt = await this.withTimeout(
      this.GTMService.callInt1(tracking.prosub_id),
      CONTINUE_LOOKUP_TIMEOUT_MS,
      "callInt1"
    );
    if (!resInt) {
      this.recoverFromContinueFailure("missing next-step route from callInt1", {
        prosub_id: tracking.prosub_id,
        next_step: null,
      });
      return;
    }

    if (resInt === "free-reg-step4c") {
      this.getProsubDetails(
        tracking.prosub_id,
        resInt,
        tracking.TP1,
        tracking.TP2,
        tracking.TP3,
        tracking.T_ID,
        { vc: tracking.vc, Other: tracking.Other, other: tracking.Other }
      );
      return;
    }

    // Navigate immediately; product details are not required to leave Account Created.
    void this.warmProsubDetails(tracking.prosub_id);
    this.navigateToNextStep(resInt, queryParams);
  }

  private buildTrackingQueryParams(data: any): {
    prosub_id: string;
    vc: unknown;
    TP1: string;
    TP2: string;
    TP3: string;
    T_ID: string;
    Other: string;
    gc: unknown;
  } {
    const prosub_id = data?.prosub_id ? data?.prosub_id : this.safeLocalStorage.getItem("prosub_id");
    const TP1: string = data?.TP1 ? data?.TP1 : data?.tp1 ? data?.tp1 : "null";
    const TP2: string = data?.TP2 ? data?.TP2 : data?.tp2 ? data?.tp2 : "null";
    const TP3: string = data[Object.keys(data)?.find((key) => key?.toLowerCase() === "tp3")] ?? "null";
    const T_ID: string = data[Object.keys(data)?.find((key) => key?.toLowerCase() === "t_id")] ?? "";
    const Other: string = data?.Other ?? data?.other ?? "";
    return {
      prosub_id,
      vc: data?.vc,
      TP1: data?.TP1 ?? data?.tp1 ?? TP1,
      TP2: data?.TP2 ?? data?.tp2 ?? TP2,
      TP3: TP3 ?? "null",
      T_ID: T_ID ?? "",
      Other,
      gc: this.gc,
    };
  }

  private async warmProsubDetails(prosub_id: string): Promise<void> {
    const prosubDetails$ = this.GTMService.getLSWProsubDetails(prosub_id);
    if (!prosubDetails$) {
      return;
    }
    try {
      const offerDetails = await firstValueFrom(
        prosubDetails$.pipe(timeout(CONTINUE_LOOKUP_TIMEOUT_MS), take(1))
      );
      const offerData = typeof offerDetails?.data === "function"
        ? offerDetails.data()
        : offerDetails?.data;
      if (offerData) {
        this.GTMService.userProduct$.next(this.GTMService.formPaymentDataForTopUp(offerData));
        if (!this.GTMService.prosubDetails) {
          this.GTMService.prosubDetails = offerData;
        }
      }
    } catch {
      // Background warm only — continue navigation does not depend on this for non-4c.
    }
  }

  private async prefetchPartialReg(prosub_id: string): Promise<void> {
    const authService = this.injector.get(AuthPartialRegService);
    // Prefer the Join Now–started journey prefetch so this await is usually a cache hit.
    const partialReg$ =
      authService.getJourneyPartialRegPrefetch(prosub_id) ??
      authService.partial_Reg(prosub_id, "", "");
    if (!partialReg$) {
      throw Object.assign(new Error("partial_Reg unavailable"), { code: "PARTIAL_REG_UNAVAILABLE" });
    }
    this.prefetchedPartialRegState = await firstValueFrom(
      partialReg$.pipe(timeout(CONTINUE_LOOKUP_TIMEOUT_MS), take(1))
    );
    this.hasPrefetchedPartialReg = true;
  }

  private getProsubDetails(prosub_id: any, resInt: string, TP1: string, TP2: string, TP3: string, T_ID: string, data: any) {
    const prosubDetails$ = this.GTMService.getLSWProsubDetails(prosub_id);
    if (!prosubDetails$) {
      this.recoverFromContinueFailure("getLSWProsubDetails unavailable", {
        prosub_id,
        next_step: resInt,
      });
      return;
    }

    // If the stream completes with no next/error, isRedirected must not stick.
    // Mark handled on next or catchError so success / error recovery are not double-handled on complete.
    let prosubHandled = false;
    this.continueProsubSub?.unsubscribe();
    this.continueProsubSub = prosubDetails$
      .pipe(
        timeout(CONTINUE_LOOKUP_TIMEOUT_MS),
        take(1),
        catchError((err) => {
          prosubHandled = true;
          this.recoverFromContinueFailure(err, { prosub_id, next_step: resInt });
          return EMPTY;
        })
      )
      .subscribe({
        next: (offerDetails: any) => {
          prosubHandled = true;
          const offerData = typeof offerDetails?.data === "function"
            ? offerDetails.data()
            : offerDetails?.data;
          if (offerData) {
            this.GTMService.userProduct$.next(this.GTMService.formPaymentDataForTopUp(offerData));
          }
          const Other: string = data?.Other ?? data?.other ?? "";
          const queryParams = {
            prosub_id: prosub_id,
            vc: data?.vc,
            TP1: TP1,
            TP2: TP2,
            TP3: TP3,
            T_ID: T_ID,
            Other: Other,
            gc: this.gc,
          };

          if (resInt === "free-reg-step4c") {
            const authService = this.injector.get(AuthPartialRegService);
            const partialReg$ =
              authService.getJourneyPartialRegPrefetch(prosub_id) ??
              authService.partial_Reg(prosub_id, "", "");
            if (!partialReg$) {
              this.recoverFromContinueFailure("partial_Reg unavailable", {
                prosub_id,
                next_step: resInt,
              });
              return;
            }
            let partialRegHandled = false;
            this.continuePartialRegSub?.unsubscribe();
            this.continuePartialRegSub = partialReg$
              .pipe(
                timeout(CONTINUE_LOOKUP_TIMEOUT_MS),
                take(1),
                catchError((err) => {
                  partialRegHandled = true;
                  this.recoverFromContinueFailure(err, { prosub_id, next_step: resInt });
                  return EMPTY;
                })
              )
              .subscribe({
                next: (res) => {
                  partialRegHandled = true;
                  this.navigateToNextStep(resInt, queryParams, res);
                },
                complete: () => {
                  if (!partialRegHandled) {
                    this.recoverFromContinueFailure("partial_Reg completed without value", {
                      prosub_id,
                      next_step: resInt,
                    });
                  }
                },
              });
            return;
          }

          this.navigateToNextStep(resInt, queryParams);
        },
        complete: () => {
          if (!prosubHandled) {
            this.recoverFromContinueFailure("getLSWProsubDetails completed without value", {
              prosub_id,
              next_step: resInt,
            });
          }
        },
      });
  }

  private navigateToNextStep(routePath: string, queryParams: Record<string, unknown>, state?: unknown) {
    if (!routePath) {
      this.recoverFromContinueFailure("missing next-step route / optional_page", {
        prosub_id: queryParams?.["prosub_id"],
        next_step: null,
      });
      return;
    }

    this.router
      .navigate([routePath], state !== undefined ? { queryParams, state } : { queryParams })
      .then((navigated) => {
        if (!navigated) {
          this.recoverFromContinueFailure(`navigation rejected for ${routePath}`, {
            prosub_id: queryParams?.["prosub_id"],
            next_step: routePath,
          });
          return;
        }
        this.isRedirected = false;
      })
      .catch((err) => {
        this.recoverFromContinueFailure(err, {
          prosub_id: queryParams?.["prosub_id"],
          next_step: routePath,
        });
      });
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        setTimeout(() => {
          reject(Object.assign(new Error(`${label} timed out after ${ms}ms`), { code: "TIMEOUT" }));
        }, ms);
      }),
    ]);
  }

  private recoverFromContinueFailure(
    err?: unknown,
    context?: { prosub_id?: unknown; next_step?: unknown }
  ): void {
    this.isRedirected = false;
    this.continueError = true;
    this.ctaResetKey += 1;

    const translated =
      this.translate.instant("registration-confirmation.continue_failed") ||
      "We couldn't continue. Please try again.";
    const message =
      translated === "registration-confirmation.continue_failed"
        ? "We couldn't continue. Please try again."
        : translated;

    this.authService.showAnonymousUserMessage(message);
    console.error("[account-created] continue failed", {
      prosub_id:
        context?.prosub_id ??
        this.route?.snapshot?.queryParams?.["prosub_id"] ??
        this.safeLocalStorage.getItem("prosub_id"),
      msn: this.authService.userMSN$?.value || this.safeLocalStorage.getItem("msn") || null,
      user_id: this.safeLocalStorage.getItem(this.authService.userIdKey) || this.safeLocalStorage.getItem("userId") || null,
      next_step: context?.next_step ?? null,
      error_code: this.getErrorCode(err),
      error: err,
    });
  }

  private getErrorCode(err: unknown): string {
    if (err == null) {
      return "unknown";
    }
    if (typeof err === "string") {
      return err;
    }
    if (err instanceof TimeoutError) {
      return "TIMEOUT";
    }
    if (err instanceof Error) {
      const withCode = err as Error & { code?: string };
      if (withCode.code) {
        return String(withCode.code);
      }
      if (err.message?.includes("timed out")) {
        return "TIMEOUT";
      }
      return err.message || err.name || "Error";
    }
    const asObj = err as { code?: string; status?: string | number; message?: string };
    if (asObj?.code) {
      return String(asObj.code);
    }
    if (asObj?.status != null) {
      return String(asObj.status);
    }
    if (asObj?.message) {
      return String(asObj.message);
    }
    return "unknown";
  }
}
